import React, { useEffect, useContext, useCallback, lazy, Suspense, useMemo, useState, useRef } from 'react';
// Assuming all imports are now correctly structured relative to the root or available globally
import ThemeContext from '../context/ThemeContext';
import articlesData from '../data/javascripts/articlesData.js';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
const ArticleCard = lazy(() => import('../widgets/ArticleCard')); 

// --- Pagination Configuration ---
const ARTICLES_PER_PAGE = 9;

const AllArticles = ({ featuredOnly = false }) => {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    const { darkMode } = useContext(ThemeContext);
    const navigate = useNavigate();
    
    const loadMoreRef = useRef(null); 
    const [displayCount, setDisplayCount] = useState(ARTICLES_PER_PAGE);
    const [isLoading, setIsLoading] = useState(false); 

    const handleArticleClick = useCallback((path) => {
        navigate(`/articles/${path}`);
    }, [navigate]);

    // --- Filter articles and memoize the full list (Unchanged) ---
    const allFilteredArticles = useMemo(() => {
        return featuredOnly
            ? articlesData.filter(article => article.featured === true)
            : articlesData;
    }, [featuredOnly]);
    
    const articlesToDisplay = useMemo(() => {
        return allFilteredArticles.slice(0, displayCount);
    }, [allFilteredArticles, displayCount]);

    const hasMore = articlesToDisplay.length < allFilteredArticles.length;

    // --- Load More Handler (Unchanged) ---
    const loadMore = useCallback(() => {
        if (hasMore && !isLoading) {
            setIsLoading(true);
            setTimeout(() => {
                setDisplayCount(prevCount => prevCount + ARTICLES_PER_PAGE);
                setIsLoading(false);
            }, 500); 
        }
    }, [hasMore, isLoading]);
    
    // --- Infinite Scroll Logic (Unchanged) ---
    useEffect(() => {
        if (!hasMore || !loadMoreRef.current) return;

        const observerTarget = loadMoreRef.current; 

        const observer = new IntersectionObserver(
            (entries) => {
                const target = entries[0];
                if (target.isIntersecting && !isLoading) {
                    loadMore();
                }
            },
            {
                root: null, 
                rootMargin: '100px',
                threshold: 0.1,
            }
        );

        observer.observe(observerTarget);

        return () => {
            if (observerTarget) {
                observer.unobserve(observerTarget);
            }
        };
    }, [hasMore, isLoading, loadMore]); 

    // --- SEO Metadata (Unchanged) ---
    const pageTitle = featuredOnly
        ? 'Featured Writings | Selected Articles'
        : 'All Articles & Blog Posts | Technical Writings';

    const pageDescription = featuredOnly
        ? 'A curated selection of the most impactful and recent featured articles.'
        : 'Explore the complete archive of technical articles, deep dives, and blog posts covering various technology and development topics.';

    const keywords = featuredOnly
        ? 'featured articles, selected writings, top blog posts, recommended reading'
        : 'all articles, blog posts, technical writings, development blog, full archive, technology articles';

    const sectionHeadingClass = `section-heading mb-2 ${darkMode ? 'text-secondary' : 'text-primary'}`;
    
    // --- Fallback/Loading UI (Unchanged) ---
    const SuspenseFallback = (
        <div className="text-center py-12">
            <div className="spinner-border w-10 h-10 border-4 border-primary dark:border-secondary border-t-transparent animate-spin rounded-full" role="status">
                <span className="sr-only">Loading...</span>
            </div>
        </div>
    );
    
    // --- Render Article Item (Unchanged) ---
    const renderArticleItem = useCallback((article) => (
        <div key={article.id || article.path} className="w-full h-full">
            <ArticleCard
                article={article}
                onClick={handleArticleClick} 
            />
        </div>
    ), [handleArticleClick]);

    // --- Main Component JSX ---
    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywords} />
            </Helmet>
            
            <div className="bg-bg-light dark:bg-bg-dark transition-colors duration-300">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16">
                    
                    {/* --- Header Section --- */}
                    <div className="text-center mb-10">
                        <h2 className={sectionHeadingClass}>
                            {featuredOnly ? 'Featured Writings' : 'My Writings'}
                        </h2>
                        {featuredOnly && allFilteredArticles.length === 0 && (
                            <p className={`text-lg ${darkMode ? 'text-muted-dark' : 'text-muted-light'}`}>
                                No featured articles available at the moment.
                            </p>
                        )}
                    </div>

                    {/* --- Articles Grid --- */}
                    <Suspense fallback={SuspenseFallback}>
                        <div 
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch"
                        >
                            {articlesToDisplay.map((article) => (
                                renderArticleItem(article)
                            ))}
                        </div>
                    </Suspense>

                    {/* --- Infinite Scroll Anchor / Loading Indicator --- */}
                    {hasMore ? (
                        <div ref={loadMoreRef} className="text-center mt-12">
                             <div className="flex justify-center items-center p-4">
                                <div className="w-8 h-8 border-4 border-primary dark:border-secondary border-t-transparent animate-spin rounded-full"></div>
                                <p className={`ml-3 text-lg ${darkMode ? 'text-muted-dark' : 'text-muted-light'}`}>
                                    Loading more...
                                </p>
                            </div>
                        </div>
                    ) : (
                        allFilteredArticles.length > ARTICLES_PER_PAGE && (
                            <div className="text-center mt-12">
                                <p className={`text-lg ${darkMode ? 'text-muted-dark' : 'text-muted-light'}`}>
                                    You've reached the end of the archive.
                                </p>
                            </div>
                        )
                    )}
                    
                </div>
            </div>
        </>
    );
};

export default React.memo(AllArticles);